import cx from 'classnames';
import React from 'react';
import AreaChart, { LineSeriesPoint } from 'ui/elements/charts/AreaChart';
import BarChart from 'ui/elements/charts/BarChart';
import { DisplayType, IndicatorType, KpiValue } from 'domain/kpis/types';
import { asUnixMillis } from 'util/dateUtils';
import { formatAsKpi, getTrend, sortKpiValuesDesc, getComparisonValue, calculateComparison } from './utils';
import ChevronUpIcon from 'ui/elements/icons/ChevronUpIcon';
import ChevronDownIcon from 'ui/elements/icons/ChevronDownIcon';
import ChevronRightIcon from 'ui/elements/icons/ChevronRightIcon';
import { bluePlanetTheme } from 'ui/theme';
import { darken } from '@mui/material';
import { format } from 'date-fns';

export interface GraphMetric {
  x: number;
  y: number | string;
}

const getGraphData = (value: KpiValue[]) =>
  value
    .map(
      (metric: KpiValue): LineSeriesPoint => ({
        x: asUnixMillis(new Date(metric.timestamp)),
        y: metric.value,
      }),
    )
    .sort((a: GraphMetric, b: GraphMetric) => a.x - b.x);

interface KPIGraphProps {
  name?: string;
  unitOfMeasure?: string;
  values?: KpiValue[];
  displayType?: DisplayType;
  trendIndicator?: IndicatorType;
  showXAxis?: boolean;
  showYAxis?: boolean;
  comparisonEnabled?: boolean;
  comparisonPeriod?: 'Month' | 'Year';
  dataPointCount?: number; // Number of main data points to display
}

export default function KPIGraph(props: KPIGraphProps) {
  const kpiValues = props.values ?? [];

  // For bar charts with comparison, limit main metrics to dataPointCount but keep all values for comparison lookup
  const mainValues =
    props.displayType === 'BarChart' && props.comparisonEnabled && props.dataPointCount
      ? kpiValues.slice(-props.dataPointCount)
      : kpiValues;

  const metrics = getGraphData(mainValues);
  const values = sortKpiValuesDesc(kpiValues).map(m => m.value);
  const direction = props.trendIndicator ? getTrend(props.trendIndicator, kpiValues) : 'neutral';
  const value = values[0] || 0;

  const displayType = props.displayType || 'NumberIndicator';

  // Calculate comparison if enabled
  let comparisonPercentage: number | null = null;
  let comparisonLabel = '';
  let currentPeriodLabel = '';

  // Always show current period if we have values
  if (kpiValues.length > 0) {
    const sortedValues = sortKpiValuesDesc(kpiValues);
    const currentTimestamp = sortedValues[0].timestamp;
    const currentDate = new Date(currentTimestamp);
    currentPeriodLabel = format(currentDate, 'MMM yyyy');

    // Calculate comparison only if enabled
    if (props.comparisonEnabled && props.comparisonPeriod && kpiValues.length >= 2) {
      const currentValue = sortedValues[0].value;
      const comparisonData = getComparisonValue(kpiValues, props.comparisonPeriod, currentTimestamp);

      if (comparisonData !== null) {
        comparisonPercentage = calculateComparison(currentValue, comparisonData.value);

        // Generate simple comparison label
        if (props.comparisonPeriod === 'Month') {
          comparisonLabel = 'from last month';
        } else if (props.comparisonPeriod === 'Year') {
          comparisonLabel = 'from last year';
        }
      }
    }
  }

  // Determine comparison color based on trend indicator
  const getComparisonColor = () => {
    if (comparisonPercentage === null || comparisonPercentage === 0) {
      return bluePlanetTheme.bluePlanetPalette.blue.medium;
    }

    const isIncreasing = comparisonPercentage > 0;
    const isPositive = props.trendIndicator === 'Up' ? isIncreasing : !isIncreasing;

    return isPositive
      ? darken(bluePlanetTheme.bluePlanetPalette.green.main, 0.2)
      : bluePlanetTheme.bluePlanetPalette.red.medium;
  };

  // Render NumberIndicator without chart
  if (displayType === 'NumberIndicator') {
    return (
      <>
        <h5 className="text-h4">{props.name}</h5>
        {currentPeriodLabel && (
          <div className="text-metadata u-color-grey-dark" style={{ marginTop: '2rem' }}>
            {currentPeriodLabel}
          </div>
        )}
        <div className="text-h2" style={{ marginTop: '0.25rem' }}>
          {formatAsKpi(value)}
          {props.unitOfMeasure && <span className="u-quarter-spacing-left text-metadata">{props.unitOfMeasure}</span>}
        </div>
        {props.comparisonEnabled && comparisonPercentage !== null && (
          <div className="u-flex u-flex-align-center" style={{ marginTop: '0.5rem' }}>
            <span
              className="text-weight-medium u-flex-align-center"
              style={{
                color: getComparisonColor(),
              }}
            >
              {comparisonPercentage > 0 ? (
                <ChevronUpIcon />
              ) : comparisonPercentage < 0 ? (
                <ChevronDownIcon />
              ) : (
                <ChevronRightIcon />
              )}
              {isFinite(comparisonPercentage) && <>{Math.abs(comparisonPercentage)}%</>}
              {comparisonLabel && <span className="u-quarter-spacing-left">{comparisonLabel}</span>}
            </span>
          </div>
        )}
      </>
    );
  }

  const renderChart = () => {
    switch (displayType) {
      case 'BarChart': {
        // Prepare comparison data if enabled
        let comparisonData: { data: { x: number; y: number }[] } | undefined;

        if (props.comparisonEnabled && props.comparisonPeriod && kpiValues.length >= 2) {
          // Get all timestamps from main data and find their comparison values
          const comparisonPoints = metrics
            .map(point => {
              const timestamp = new Date(point.x).toISOString();
              const comparisonValue = getComparisonValue(kpiValues, props.comparisonPeriod!, timestamp);

              if (comparisonValue) {
                return {
                  x: point.x, // Use the same x-value for side-by-side bars
                  y: comparisonValue.value,
                };
              }
              return null;
            })
            .filter((point): point is { x: number; y: number } => point !== null);

          if (comparisonPoints.length > 0) {
            comparisonData = { data: comparisonPoints };
          }
        }

        return (
          <BarChart
            name=""
            main={{
              data: metrics,
            }}
            comparison={comparisonData}
            unit={props.unitOfMeasure}
            showXAxis={props.showXAxis}
            showYAxis={props.showYAxis}
          />
        );
      }
      case 'LineChart':
      default:
        return (
          <AreaChart
            name=""
            main={{
              data: metrics,
            }}
            unit={props.unitOfMeasure}
            showXAxis={props.showXAxis}
            showYAxis={props.showYAxis}
          />
        );
    }
  };

  return (
    <>
      <h5 className="text-h4">{props.name}</h5>
      <div
        className={cx(
          {
            'u-color-green': direction === 'positive',
            'u-color-red': direction === 'negative',
          },
          'text-h2',
          'u-align-right',
        )}
      >
        {formatAsKpi(value)}
        {props.unitOfMeasure && <span className="u-quarter-spacing-left text-metadata">{props.unitOfMeasure}</span>}
      </div>
      {renderChart()}
    </>
  );
}
